import HeaderLogo from '../../assets/images/headerLogo.png';
import HistoryImg from '../../assets/images/history.png';
import { useEffect, useRef, useState, memo, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useLocation, useNavigate } from 'react-router-dom';
import droneImg from '../../assets/images/eos-icons_drone.png';
import CancelJobModal from '../Models/CancelJobModal';
import RecentActivityPanel from '../Models/RecentActivityPanel';
import ViewReport from '../Models/ViewReport';
import ViewSummary from '../Models/ViewSummary';
import alertImg from "../../assets/images/mdi_alert-box.png";
import { Inspection } from '../../services/inspection.service';
import { differenceInHours, differenceInMinutes } from 'date-fns';
import InspectionModal from '../Models/InspectionModal';
import { ToastContainer } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/reducers';
import { actions } from '../../store/actions';
import { useInspectionPolling } from '../../hooks/useInspectionPolling';
import { useActivityMonitoring } from '../../hooks/useActivityMonitoring';
import DroneStatus from '../Models/DroneStatus';

// Header Content Component - Merged all header sections
const HeaderContent = memo(({
    user,
    currentInspection,
    hasCriticalEvent,
    hasNewActivity,
    setShowSummary,
    setShowRecentActivity,
    setIsCancleJob,
    onCriticalEventClick,
    onLogout,
    onShowRecentActivity,
    currentPath
}: {
    user: any;
    currentInspection: Inspection | null;
    hasCriticalEvent: boolean;
    hasNewActivity: boolean;
    setShowSummary: (value: boolean) => void;
    setShowRecentActivity: (value: boolean) => void;
    setIsCancleJob: (value: boolean) => void;
    onCriticalEventClick: () => void;
    onLogout: () => void;
    onShowRecentActivity: () => void;
    currentPath: string;
}) => {
    const navigate = useNavigate();
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [showDroneStatus, setShowDroneStatus] = useState(false);
    const [showAddYard, setShowAddYard] = useState(false);
    const [timeElapsed, setTimeElapsed] = useState('');
    const userDropdownRef = useRef<HTMLDivElement>(null);
    const isDashboardActive = currentPath === '/dashboard';

    // Handle dropdown outside clicks
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
                setShowUserDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Update time elapsed
    useEffect(() => {
        if (!currentInspection) return;

        const updateTime = () => {
            const start = new Date(currentInspection.createdAt);
            const now = new Date();
            const hours = differenceInHours(now, start);
            const minutes = differenceInMinutes(now, start) % 60;
            setTimeElapsed(hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`);
        };

        updateTime();
        const interval = setInterval(updateTime, 60000);
        return () => clearInterval(interval);
    }, [currentInspection]);

    // Generate user initials
    const getUserInitials = (name: string) => {
        return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
    };

    // Format status
    const formatStatus = (status: string) => {
        return status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    return (
        <>
            {/* Top Header Section */}
            <header className="w-full">
                <div className="bg-green flex flex-col sm:flex-row flex-wrap sm:flex-nowrap items-center justify-between rounded-br-[4px] rounded-bl-[4px] p-2 sm:p-3">
                    {/* Logo Section */}
                    <div className="flex justify-center sm:justify-start w-full sm:w-auto mb-1 sm:mb-0">
                        <img
                            src={HeaderLogo}
                            alt="Logo"
                            className="w-[120px] sm:w-[140px] md:w-[170px] lg:w-[200px] xl:w-[220px]"
                        />
                    </div>

                    {/* Navigation Section */}
                    <nav className="flex flex-wrap justify-center items-center gap-1 sm:gap-2 md:gap-3 lg:gap-4 xl:gap-5 text-light-green font-normal text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl w-full sm:w-auto">
                        <a href="/dashboard" className={`font-[Geist] whitespace-nowrap ${isDashboardActive ? 'font-bold text-white' : 'hover:font-bold hover:text-white'}`}>
                            Dashboard
                        </a>
                        <a href="/inventory" className={`font-[Geist] whitespace-nowrap ${currentPath === '/inventory' ? 'font-bold text-white' : 'hover:font-bold hover:text-white'}`}>
                            Inventory
                        </a>
                        <a href="/inspection" className={`font-[Geist] whitespace-nowrap ${currentPath === '/inspection' ? 'font-bold text-white' : 'hover:font-bold hover:text-white'}`}>
                            Inspection
                        </a>

                    </nav>

                    {/* User Info Section */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 w-full sm:w-auto mr-2 sm:mr-4">
                        <div className="relative flex gap-1 sm:gap-2 bg-white px-2 sm:px-3 py-1 sm:py-2 rounded items-center">
                            <img src={HistoryImg} alt="History" className="size-3 sm:size-4" />
                            <button onClick={onShowRecentActivity} className="text-gray font-[Geist-Medium] text-xs sm:text-sm whitespace-nowrap">
                                Recent Activity
                            </button>
                            {hasNewActivity && (
                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white animate-pulse"></div>
                            )}
                        </div>

                        {/* User Dropdown */}
                        <div className="relative" ref={userDropdownRef}>
                            <button
                                onClick={() => setShowUserDropdown(!showUserDropdown)}
                                className="flex items-center gap-3 text-white hover:bg-white hover:bg-opacity-15 px-4 py-3 rounded-lg transition-all duration-300 ease-in-out transform hover:scale-105"
                            >
                                <div className="w-10 h-10 bg-gradient-to-br from-white to-white/80 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white/20">
                                    <span className="text-green font-bold text-lg">
                                        {user?.name ? getUserInitials(user.name) : 'U'}
                                    </span>
                                </div>
                                <div className="flex flex-col items-start">
                                    <span className="text-sm text-white font-bold font-[Geist-Bold] whitespace-nowrap">
                                        {user?.name || 'User'}
                                    </span>
                                    <span className="text-xs text-white/90 font-[Geist] whitespace-nowrap">
                                        Welcome!
                                    </span>
                                </div>
                                <Icon
                                    icon="mdi:chevron-down"
                                    className={`text-white transition-all duration-300 ease-in-out ${showUserDropdown ? 'rotate-180' : ''}`}
                                />
                            </button>

                            {showUserDropdown && (
                                <div className="absolute top-full right-0 mt-2 w-48 bg-white text-black rounded-lg shadow-xl border border-green/20 z-20 animate-in slide-in-from-top-2 duration-200">
                                    <div className="px-3 py-2 border-b border-green/10 bg-green/5">
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 bg-gradient-to-br from-green to-green-600 rounded-full flex items-center justify-center">
                                                <span className="text-white font-bold text-xs">
                                                    {user?.name ? getUserInitials(user.name) : 'U'}
                                                </span>
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-gray-900">{user?.name || 'User'}</p>
                                                <p className="text-xs text-gray-500">Admin</p>
                                            </div>
                                        </div>
                                    </div>
                                    <ul className="py-1">
                                        <li>
                                            <a
                                                href="/profile"
                                                onClick={() => setShowUserDropdown(false)}
                                                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200 flex items-center gap-2 group"
                                            >
                                                <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                                                    <Icon icon="mdi:account" className="text-sm text-gray-600" />
                                                </div>
                                                <span className="font-medium">Profile</span>
                                            </a>
                                        </li>
                                        <li className="border-t border-green/10">
                                            <button
                                                onClick={() => { setShowUserDropdown(false); onLogout(); }}
                                                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-all duration-200 flex items-center gap-2 group"
                                            >
                                                <div className="w-6 h-6 rounded bg-red-100 flex items-center justify-center group-hover:bg-red-200 transition-colors">
                                                    <Icon icon="mdi:logout" className="text-sm text-red-600" />
                                                </div>
                                                <span className="font-medium">Logout</span>
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Bottom Header Section */}
            <div className="flex justify-between items-center py-2 gap-4 w-full">
                {/* Company Info */}
                <div className='flex min-w-max max-w-max px-2'>
                    <div className="pr-3 xl:pr-4 border-r border-gray-3 flex flex-col gap-1">
                        <div className="flex items-center">
                            <h1 className="text-black text-[18px] sm:text-[20px] xl:text-[22px] font-[Geist-Bold] tracking-tight">
                                {user?.companyName}
                            </h1>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1 bg-gray-1 px-2 py-0.5 rounded-md">
                                <div className="w-1 h-1 bg-blue rounded-full"></div>
                                <span className="text-xs text-gray-2 font-[Geist-Medium]">
                                    DOT: <span className="text-black font-[Geist-SemiBold]">{user?.dot}</span>
                                </span>
                            </div>
                            <div className="flex items-center gap-1 bg-gray-1 px-2 py-0.5 rounded-md">
                                <div className="w-1 h-1 bg-green rounded-full"></div>
                                <span className="text-xs text-gray-2 font-[Geist-Medium]">
                                    Willow ID: <span className="text-green font-[Geist-SemiBold]">{user?.willowId}</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Job Details */}
                <div className='flex flex-col xl:flex-row w-full justify-between items-center gap-4'>
                    <div className="flex flex-wrap items-center gap-4 text-sm xl:text-base max-w-full">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                            {/* Current Job */}
                            <div className="flex items-center gap-2">
                                <span className="text-black-1 font-[Geist-semibold] text-sm">Current Job:</span>
                                <span className={`text-xs px-2 py-1 rounded font-[Geist-medium] ${!currentInspection ? "text-gray-2 border border-gray-3 bg-gray-1" : "text-green border border-green bg-green-1"}`}>
                                    {currentInspection ? "Inspection" : "N/A"}
                                </span>
                            </div>

                            {/* Status */}
                            <div className="flex items-center gap-2">
                                <span className="text-black-1 font-[Geist-semibold] text-sm">Status:</span>
                                {currentInspection ? (
                                    <div className="flex items-center">
                                        <span className="text-green font-[Acumin-medium]">
                                            {formatStatus(currentInspection.status)}
                                        </span>
                                        {currentInspection.status === 'in-progress' && (
                                            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-green ml-2"></div>
                                        )}
                                    </div>
                                ) : (
                                    <span className="text-xs px-2 py-1 rounded font-[Geist-medium] text-gray-2 border border-gray-3 bg-gray-1">
                                        N/A
                                    </span>
                                )}
                            </div>

                            {/* Start Time */}
                            <div className="flex items-center gap-2">
                                <span className="text-black-1 font-[Geist-semibold] text-sm">Start Time:</span>
                                {currentInspection ? (
                                    <span className="text-green font-[Acumin-medium]">
                                        {timeElapsed}
                                    </span>
                                ) : (
                                    <span className="text-xs px-2 py-1 rounded font-[Geist-medium] text-gray-2 border border-gray-3 bg-gray-1">
                                        N/A
                                    </span>
                                )}
                            </div>
                        </div>

                        {currentInspection && (
                            <>
                                <div className="shrink-0">
                                    <button
                                        onClick={() => setIsCancleJob(true)}
                                        className="flex items-center gap-1.5 bg-blue hover:bg-blue-600 text-[#EBEBEB] px-3 py-1.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                                    >
                                        <Icon icon="mdi:cancel" width={14} height={14} />
                                        <span className="text-sm font-[Geist-SemiBold]">Cancel Job</span>
                                    </button>
                                </div>
                                {currentInspection?.status === 'failed' && hasCriticalEvent && (
                                    <div className="shrink-0">
                                        <button
                                            onClick={onCriticalEventClick}
                                            className="flex items-center gap-2 bg-[#FFFAEB] hover:bg-[#FFF4D6] text-[#CC9800] border border-[#CC9800] px-4 py-2 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 animate-pulse"
                                        >
                                            <img src={alertImg} alt="" className="w-4 h-4" />
                                            <span className="text-sm font-[Geist-Bold]">Critical Event Detected</span>
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 items-center shrink-0">
                    <button
                        onClick={() => setShowDroneStatus(true)}
                        className="flex gap-1.5 items-center bg-gray-4 text-gray-400 px-3 py-1.5 rounded-lg"
                    >
                        <img src={droneImg} alt="Drone" className='size-[16px]' />
                        <span className='text-sm font-[Geist-SemiBold]'>Watch Drone</span>
                    </button>
                    <button
                        onClick={() => navigate('/yard-designer')}
                        className="flex gap-1.5 items-center bg-gray-4 text-gray-400 px-3 py-1.5 rounded-lg transition hover:bg-gray-3"
                    >
                        <Icon icon="pajamas:plus" width={16} height={16} />
                        <span className="text-sm font-[Geist-SemiBold]">Add Yard</span>
                    </button>
                </div>
            </div>

            {showDroneStatus && (
                <DroneStatus onClose={() => setShowDroneStatus(false)} />
            )}

            {showAddYard && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="w-full max-w-[520px] rounded-lg bg-white shadow-xl">
                        <div className="flex items-start justify-between border-b px-6 py-5">
                            <div>
                                <h2 className="font-[Geist-Bold] text-xl text-green">
                                    Add Yard
                                </h2>
                                <p className="mt-1 text-sm text-gray-2">
                                    Yard registration will be connected here.
                                </p>
                            </div>

                            <button
                                onClick={() => setShowAddYard(false)}
                                className="text-gray-2 transition hover:text-black"
                                aria-label="Close Add Yard"
                            >
                                <Icon icon="material-symbols:close" className="text-2xl" />
                            </button>
                        </div>

                        <div className="px-6 py-6">
                            <p className="text-sm text-gray-2">
                                The Add Yard interface is ready for the yard registration workflow.
                            </p>
                        </div>

                        <div className="flex justify-end border-t px-6 py-4">
                            <button
                                onClick={() => setShowAddYard(false)}
                                className="rounded bg-blue px-5 py-2 text-sm font-[Geist-SemiBold] text-white transition hover:bg-blue-1"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
});

export interface HeaderProps {
    shouldRefreshInspection: boolean;
    onInspectionRefreshed: () => void;
}

export default function Header({ shouldRefreshInspection, onInspectionRefreshed }: HeaderProps) {
    const dispatch = useDispatch();
    const [isCancleJOb, setIsCancleJob] = useState(false);
    const [showReport, setShowReport] = useState(false);
    const [showSummary, setShowSummary] = useState(false);
    const [showRecentActivity, setShowRecentActivity] = useState(false);
    const [showInspectionModal, setShowInspectionModal] = useState(false);

    // Get state from Redux
    const { user } = useSelector((e: RootState) => ({ user: e.user }));
    const { currentInspection, completedInspection, hasCriticalEvent, hasModelOpen, hasNewActivity } = useSelector((state: RootState) => state.inspection);

    const routerLocation = useLocation();
    const { fetchInProgressInspection, checkInspectionImmediately } = useInspectionPolling();
    
    // Start activity monitoring
    useActivityMonitoring();

    // Effect to handle inspection refresh requests
    useEffect(() => {
        if (shouldRefreshInspection) {
            fetchInProgressInspection();
            onInspectionRefreshed();
        }
    }, [shouldRefreshInspection, fetchInProgressInspection, onInspectionRefreshed]);

    // Effect to automatically open ViewReport when critical event is detected
    useEffect(() => {
        if (hasCriticalEvent && currentInspection && !hasModelOpen) {
            dispatch(actions.modelOpen(true));
            setShowReport(true);
        }
    }, [hasCriticalEvent]);

    const handleCancelSuccess = useCallback(() => {
        dispatch(actions.clearInspectionData());
        fetchInProgressInspection();
    }, [dispatch, fetchInProgressInspection]);

    const handleInspectionCreated = useCallback(() => {
        checkInspectionImmediately();
    }, [checkInspectionImmediately]);

    const handleCriticalEventClick = useCallback(() => {
        if (currentInspection) {
            dispatch(actions.setCompletedInspection(currentInspection));
            setShowReport(true);
        }
    }, [dispatch, currentInspection]);

    const handleInspectionUpdated = useCallback(() => {
        fetchInProgressInspection();
    }, [fetchInProgressInspection]);

    const handleLogout = useCallback(() => {
        dispatch(actions.logout());
        window.location.href = '/login';
    }, [dispatch]);

    const handleShowRecentActivity = useCallback(() => {
        setShowRecentActivity(true);
        // Clear notification when panel is opened
        dispatch(actions.clearActivityNotification());
    }, [dispatch]);

    return (
        <>
            <HeaderContent
                user={user}
                currentInspection={currentInspection}
                hasCriticalEvent={hasCriticalEvent}
                hasNewActivity={hasNewActivity}
                setShowSummary={setShowSummary}
                setShowRecentActivity={setShowRecentActivity}
                setIsCancleJob={setIsCancleJob}
                onCriticalEventClick={handleCriticalEventClick}
                onLogout={handleLogout}
                onShowRecentActivity={handleShowRecentActivity}
                currentPath={routerLocation.pathname}
            />

            {showRecentActivity && <RecentActivityPanel onClose={() => setShowRecentActivity(false)} />}
            <ToastContainer />

            {/* Modals */}
            {showInspectionModal && (
                <InspectionModal
                    isOpen={true}
                    onClose={() => setShowInspectionModal(false)}
                    onInspectionCreated={handleInspectionCreated}
                />
            )}
            {showReport && <ViewReport onClose={() => setShowReport(false)} inspection={completedInspection} onInspectionUpdated={handleInspectionUpdated} />}
            {showSummary && <ViewSummary onClose={() => setShowSummary(false)} inspection={currentInspection} />}
            {isCancleJOb && currentInspection && (
                <CancelJobModal
                    onClose={() => setIsCancleJob(false)}
                    inspectionId={currentInspection.id}
                    onCancelSuccess={handleCancelSuccess}
                />
            )}
        </>
    );
}
